import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../../lib/api";
import PublicNav from "../../components/PublicNav";
import { CalendarBlank, MapPin, UsersThree, Copy, Check, EnvelopeSimple, CalendarPlus } from "@phosphor-icons/react";

export default function BookingDetails() {
  const { id } = useParams();
  const [state, setState] = useState({ status: "loading" });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/public/bookings/${id}`);
        if (!cancelled) setState({ status: "ok", booking: r.data });
      } catch (e) {
        if (!cancelled) setState({ status: "not_found" });
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  if (state.status === "loading") {
    return (
      <div className="public-shell">
        <PublicNav />
        <section className="bg-ms-cream pt-40 pb-32">
          <div className="max-w-2xl mx-auto px-6 text-center text-ms-stone">Loading your booking…</div>
        </section>
      </div>
    );
  }

  if (state.status === "not_found") {
    return (
      <div className="public-shell">
        <PublicNav />
        <section className="bg-ms-cream pt-40 pb-32">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <h1 className="serif text-ms-ink text-4xl">Booking not found</h1>
            <p className="text-ms-stone mt-4 leading-[1.7]">
              This link may be incorrect, or the booking hasn't been confirmed yet. If you just paid, check back in a minute or two.
            </p>
            <Link to="/" className="mt-8 inline-block px-7 py-3 border border-ms-edge text-ms-ink hover:bg-ms-ink hover:text-ms-cream transition-all rounded-full label-eyebrow text-xs">Return home</Link>
          </div>
        </section>
      </div>
    );
  }

  const b = state.booking;
  // Format the starts_at ISO string to a nice human string in the visitor's local time
  let dateLabel = "";
  let googleCalUrl = "";
  try {
    if (b.starts_at) {
      const d = new Date(b.starts_at);
      dateLabel = d.toLocaleString("en-US", {
        weekday: "long", month: "long", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit",
      });
      // Google Calendar template URL — opens a new event with our details pre-filled.
      // Format times as YYYYMMDDTHHMMSSZ in UTC
      const pad = (n) => String(n).padStart(2, "0");
      const fmt = (dt) => `${dt.getUTCFullYear()}${pad(dt.getUTCMonth()+1)}${pad(dt.getUTCDate())}T${pad(dt.getUTCHours())}${pad(dt.getUTCMinutes())}00Z`;
      const start = fmt(d);
      const endDate = new Date(d.getTime() + 4 * 60 * 60 * 1000); // assume 4h tour
      const end = fmt(endDate);
      const params = new URLSearchParams({
        action: "TEMPLATE",
        text: `Mystery Served — ${b.tour_title}`,
        dates: `${start}/${end}`,
        details: `Your private Mystery Served experience.\n\nView your booking: ${typeof window !== "undefined" ? window.location.href : ""}`,
        location: b.meeting_location || "",
      });
      googleCalUrl = `https://calendar.google.com/calendar/render?${params.toString()}`;
    }
  } catch {}

  const icsUrl = `/api/public/bookings/${b.id}/calendar.ics`;

  return (
    <div className="public-shell">
      <PublicNav />
      <section className="bg-ms-cream pt-32 md:pt-40 pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <p className="label-eyebrow text-ms-brass text-xs">Case Briefing</p>
          <h1 className="serif text-ms-ink text-4xl md:text-5xl mt-3 leading-[1.05]" data-testid="booking-tour-title">
            {b.tour_title}
          </h1>
          {b.lead_first_name && (
            <p className="text-ms-stone mt-3 text-lg leading-[1.6]">
              Reserved by <span className="text-ms-ink">{b.lead_first_name}</span> — share this page with your party.
            </p>
          )}

          {/* Detail rows */}
          <div className="bg-ms-ivory border border-ms-edge rounded-sm p-6 md:p-8 mt-10 space-y-5">
            {dateLabel && (
              <div className="flex items-start gap-4">
                <CalendarBlank size={22} weight="duotone" className="text-ms-brass mt-0.5 shrink-0" />
                <div>
                  <p className="label-eyebrow text-ms-stone text-[10px]">Date & Time</p>
                  <p className="text-ms-ink mt-1" data-testid="booking-date">{dateLabel}</p>
                </div>
              </div>
            )}
            {b.meeting_location && (
              <div className="flex items-start gap-4">
                <MapPin size={22} weight="duotone" className="text-ms-brass mt-0.5 shrink-0" />
                <div>
                  <p className="label-eyebrow text-ms-stone text-[10px]">Meeting Location</p>
                  <p className="text-ms-ink mt-1" data-testid="booking-location">{b.meeting_location}</p>
                  {b.map_pin_url ? (
                    <a href={b.map_pin_url} target="_blank" rel="noopener noreferrer"
                       data-testid="booking-map-link"
                       className="inline-flex items-center gap-1 mt-2 text-ms-brass text-sm underline hover:text-ms-ink transition-colors">
                      Open in Google Maps →
                    </a>
                  ) : (
                    <p className="text-ms-stone text-sm mt-1">Google Maps pin will arrive in your game link email 24 hours before start.</p>
                  )}
                </div>
              </div>
            )}
            <div className="flex items-start gap-4">
              <UsersThree size={22} weight="duotone" className="text-ms-brass mt-0.5 shrink-0" />
              <div>
                <p className="label-eyebrow text-ms-stone text-[10px]">Party Size</p>
                <p className="text-ms-ink mt-1" data-testid="booking-guests">{b.guest_count} {b.guest_count === 1 ? "guest" : "guests"}</p>
              </div>
            </div>
          </div>

          {/* Share + add-to-calendar */}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              onClick={copy}
              className="inline-flex items-center gap-2 px-5 py-3 bg-ms-ink text-ms-cream hover:bg-ms-ink/90 transition-all rounded-full label-eyebrow text-xs"
              data-testid="booking-share-copy"
            >
              {copied ? <Check size={16} weight="bold" /> : <Copy size={16} weight="bold" />}
              {copied ? "Link copied" : "Copy share link"}
            </button>
            {googleCalUrl && (
              <a
                href={googleCalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 border border-ms-edge text-ms-ink hover:bg-ms-ink hover:text-ms-cream transition-all rounded-full label-eyebrow text-xs"
                data-testid="booking-add-gcal"
              >
                <CalendarPlus size={16} weight="bold" /> Google Calendar
              </a>
            )}
            <a
              href={icsUrl}
              className="inline-flex items-center gap-2 px-5 py-3 border border-ms-edge text-ms-ink hover:bg-ms-ink hover:text-ms-cream transition-all rounded-full label-eyebrow text-xs"
              data-testid="booking-add-ics"
              download={`mystery-served-${b.id}.ics`}
            >
              <CalendarPlus size={16} weight="bold" /> Apple / Outlook
            </a>
          </div>

          {/* Per-party notes from the operator */}
          {b.party_notes && (
            <div className="mt-10 bg-ms-parchment border-l-4 border-ms-brass rounded-sm p-6 md:p-8" data-testid="booking-party-notes">
              <p className="label-eyebrow text-ms-brass text-[10px]">A note for your group</p>
              <p className="text-ms-ink mt-2 leading-[1.75] whitespace-pre-wrap">{b.party_notes}</p>
            </div>
          )}

          {/* How to show up */}
          <div className="mt-16">
            <p className="label-eyebrow text-ms-brass text-xs">Before You Show Up</p>
            <h2 className="serif text-ms-ink text-3xl mt-2 leading-[1.1]">Read this with your party.</h2>
            <div className="mt-8 grid md:grid-cols-2 gap-x-10 gap-y-6 text-ms-ink text-[15px] leading-[1.65]">
              <Tip n="1" title="Your Game Link">
                Arrives in a separate email <em>three hours before</em> the tour. Private to your group. First page only — GPS unlocks the rest when you arrive at the starting point.
              </Tip>
              <Tip n="2" title="Your Party">
                Private experience for your group only, six guests max. Share this page with everyone coming.
              </Tip>
              <Tip n="3" title="Come Hungry">
                Food is prepaid. You'll have a full meal across the tour.
              </Tip>
              <Tip n="4" title="Gratuity">
                Tips not included. Bring cash — about <strong>$10 per person, per stop</strong>. Our restaurant partners are the heart of this neighborhood.
              </Tip>
              <Tip n="5" title="No Human Guide">
                You'll solve it as a group. One fully charged smartphone with internet required. Notebooks provided.
              </Tip>
              <Tip n="6" title="Walks & Weather">
                Comfortable shoes. Umbrella if stormy. Tours run rain or shine — text or email if you're worried.
              </Tip>
              <Tip n="7" title="Be On Time">
                Restaurants prep food fresh. 20+ minutes late? Please text <a href="tel:7272705531" className="text-ms-brass underline">727-270-5531</a>.
              </Tip>
              <Tip n="8" title="Duration">
                Plan for about <strong>four hours</strong> start to finish.
              </Tip>
              <Tip n="9" title="Stay Charged">
                A portable phone charger is highly recommended.
              </Tip>
              <Tip n="10" title="Parking">
                We recommend <strong>Universal Parking</strong>. Skip the street meters — tour runs longer than two hours.
              </Tip>
              <Tip n="11" title="Meeting Location">
                {b.meeting_location || "See your game link email"}.
                {b.map_pin_url ? (
                  <> <a href={b.map_pin_url} target="_blank" rel="noopener noreferrer" className="text-ms-brass underline">Open in Google Maps →</a></>
                ) : (
                  <> Google Maps pin in your game link.</>
                )}
              </Tip>
              <Tip n="12" title="Check-In">
                When you arrive, tell the restaurant you're with <strong>Mystery Served</strong>.
              </Tip>
            </div>
          </div>

          {/* Contact */}
          <div className="mt-16 bg-ms-parchment border border-ms-edge rounded-sm p-6 md:p-8">
            <div className="flex items-start gap-4">
              <EnvelopeSimple size={22} weight="duotone" className="text-ms-brass mt-1 shrink-0" />
              <div>
                <p className="label-eyebrow text-ms-stone text-[10px]">Questions before tour day?</p>
                <p className="text-ms-ink mt-2 leading-[1.7]">
                  Call or text <a href="tel:7272705531" className="text-ms-brass underline">727-270-5531</a>, or email <a href="mailto:info@mysteryserved.com" className="text-ms-brass underline">info@mysteryserved.com</a>.
                </p>
                <p className="text-ms-stone text-sm mt-3 italic">— Fuji</p>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link to="/" className="inline-block px-7 py-3 border border-ms-edge text-ms-ink hover:bg-ms-ink hover:text-ms-cream transition-all rounded-full label-eyebrow text-xs">
              Return home
            </Link>
          </div>

          <p className="text-ms-stone text-xs mt-12 text-center font-mono opacity-70">Booking · {b.id}</p>
        </div>
      </section>
    </div>
  );
}

function Tip({ n, title, children }) {
  return (
    <div data-testid={`tip-${n}`}>
      <p className="label-eyebrow text-ms-brass text-[10px]">{n}</p>
      <p className="serif text-ms-ink text-lg mt-1">{title}</p>
      <p className="text-ms-stone text-sm mt-1.5 leading-[1.65]">{children}</p>
    </div>
  );
}
